import { useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import "./GameProgressChart.css";

const HIDDEN_ITEMS_LS_KEY = "vocabularium_progress_chart_hidden";

function readHiddenItems() {
  try {
    const raw = localStorage.getItem(HIDDEN_ITEMS_LS_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeHiddenItems(hiddenItems) {
  try {
    localStorage.setItem(HIDDEN_ITEMS_LS_KEY, JSON.stringify(hiddenItems));
  } catch {
    // quota / mode privé : on ignore
  }
}

const SERIES = [
  { key: "discoveredCount", name: "Découverte", color: "#1f6b45" },
  { key: "masteredCount", name: "Maîtrise", color: "#9a6b12" },
  { key: "reviewCount", name: "À revoir", color: "#991b1b" },
];

const SKELETON_X_LABELS = [
  "00/00 00:00",
  "00/00 00:00",
  "00/00 00:00",
  "00/00 00:00",
  "00/00 00:00",
  "00/00 00:00",
  "00/00 00:00",
  "00/00 00:00",
];

const SKELETON_THRESHOLDS = {
  discoveryTarget: 835,
  masteryTarget: 752,
  masteryRegressionTarget: 521,
  regressionLevelName: "Croque-chapitre",
  showRegressionLine: true,
};

const THRESHOLD_SERIES = [
  {
    key: "discoveryThreshold",
    name: "Seuil de découverte",
    color: "#1f6b45",
  },
  {
    key: "masteryThreshold",
    name: "Seuil de maîtrise",
    color: "#9a6b12",
  },
  {
    key: "masteryRegressionThreshold",
    name: "Régression de niveau",
    color: "#991b1b",
  },
];

function formatPointLabel(point, index) {
  if (point?.playedAt) {
    const date = new Date(point.playedAt);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  }
  return `Partie ${index + 1}`;
}

function ProgressChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;

  return (
    <div className="game-progress-chart__tooltip">
      <p className="game-progress-chart__tooltip-label">{label}</p>
      {payload.map((entry) => (
        <p
          key={entry.dataKey}
          className="game-progress-chart__tooltip-row"
          style={{ color: entry.color }}
        >
          {entry.name}&nbsp;: <strong>{entry.value}</strong>
        </p>
      ))}
    </div>
  );
}

function ThresholdLineLabel({ viewBox, text, fill }) {
  if (!viewBox) return null;

  return (
    <text
      x={viewBox.x + 8}
      y={viewBox.y + 8}
      fill={fill}
      fontSize={10}
      fontFamily="Georgia, serif"
      dominantBaseline="hanging"
    >
      {text}
    </text>
  );
}

function ProgressChartLegend({ items, hiddenItems, onToggle }) {
  if (!items.length) return null;

  return (
    <div className="game-progress-chart__legend" role="list" aria-label="Légende du graphique">
      {items.map((item) => {
        const isHidden = Boolean(hiddenItems[item.key]);
        return (
          <button
            key={item.key}
            type="button"
            role="listitem"
            className={`game-progress-chart__legend-item${
              isHidden ? " game-progress-chart__legend-item--hidden" : ""
            }`}
            onClick={() => onToggle(item.key)}
            aria-pressed={!isHidden}
          >
            <span
              className={`game-progress-chart__legend-swatch${
                item.dashed ? " game-progress-chart__legend-swatch--dashed" : ""
              }`}
              style={{ borderTopColor: item.color }}
              aria-hidden="true"
            />
            <span>{item.name}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function GameProgressChart({
  history = [],
  thresholds = null,
  skeleton = false,
}) {
  const [hiddenItems, setHiddenItems] = useState(() => readHiddenItems());
  const resolvedThresholds = skeleton ? SKELETON_THRESHOLDS : thresholds;

  const chartData = useMemo(() => {
    if (skeleton) {
      return SKELETON_X_LABELS.map((label) => ({
        label,
        discoveredCount: 0,
        masteredCount: 0,
        reviewCount: 0,
      }));
    }

    return (Array.isArray(history) ? history : []).map((point, index) => ({
      ...point,
      label: formatPointLabel(point, index),
      discoveredCount: point.discoveredCount ?? 0,
      masteredCount: point.masteredCount ?? 0,
      reviewCount: point.reviewCount ?? 0,
    }));
  }, [history, skeleton]);

  const discoveryTarget = resolvedThresholds?.discoveryTarget;
  const masteryTarget = resolvedThresholds?.masteryTarget;
  const masteryRegressionTarget = resolvedThresholds?.masteryRegressionTarget;
  const regressionLevelName = resolvedThresholds?.regressionLevelName;

  const showRegressionLine = Boolean(resolvedThresholds?.showRegressionLine);

  const regressionLegendLabel = regressionLevelName
    ? `Régression (${regressionLevelName}) — ${masteryRegressionTarget} pts`
    : `Régression de niveau (${masteryRegressionTarget} pts)`;

  const regressionLineLabel = regressionLevelName
    ? `Régression ${regressionLevelName} (${masteryRegressionTarget} pts)`
    : `Régression de niveau (${masteryRegressionTarget} pts)`;

  const legendItems = useMemo(() => {
    const items = SERIES.map((series) => ({ ...series, dashed: false }));

    if (showRegressionLine) {
      items.push({
        ...THRESHOLD_SERIES[2],
        name: regressionLegendLabel,
        dashed: true,
      });
    }

    if (Number.isFinite(discoveryTarget)) {
      items.push({ ...THRESHOLD_SERIES[0], dashed: true });
    }
    if (Number.isFinite(masteryTarget)) {
      items.push({ ...THRESHOLD_SERIES[1], dashed: true });
    }

    return items;
  }, [discoveryTarget, masteryTarget, regressionLegendLabel, showRegressionLine]);

  const handleLegendToggle = (itemKey) => {
    setHiddenItems((prev) => {
      const next = { ...prev, [itemKey]: !prev[itemKey] };
      writeHiddenItems(next);
      return next;
    });
  };

  if (!skeleton && chartData.length < 2) {
    return (
      <div className="game-progress-chart game-progress-chart--empty">
        <p className="game-progress-chart__empty-title">Historique en cours de constitution</p>
        <p className="game-progress-chart__empty-text">
          Le graphique apparaîtra dès que deux parties auront enregistré l&apos;évolution de
          vos scores de découverte et de maîtrise sur le pool du niveau en cours.
        </p>
      </div>
    );
  }

  return (
    <section
      className={`game-progress-chart${skeleton ? " game-progress-chart--skeleton" : ""}`}
      aria-hidden={skeleton || undefined}
      aria-label={
        skeleton
          ? undefined
          : "Évolution des scores de découverte et de maîtrise sur le pool du niveau en cours"
      }
    >
      <ProgressChartLegend
        items={legendItems}
        hiddenItems={skeleton ? {} : hiddenItems}
        onToggle={handleLegendToggle}
      />
      <div className="game-progress-chart__chart-wrap">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 8, right: 8, left: 8, bottom: 0 }}
            style={{ background: "transparent" }}
          >
            <CartesianGrid
              stroke="rgba(57, 33, 13, 0.12)"
              strokeDasharray="3 3"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={{ fill: "rgba(57, 33, 13, 0.72)", fontSize: 10 }}
              tickLine={false}
              axisLine={{ stroke: "rgba(57, 33, 13, 0.18)" }}
              minTickGap={24}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fill: "rgba(57, 33, 13, 0.72)", fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              width={36}
              allowDecimals={false}
              ticks={skeleton ? [0, 250, 500, 750, 875] : undefined}
              domain={
                skeleton
                  ? [0, 875]
                  : [
                      0,
                      (maxValue) => {
                        const seriesMax = Math.max(
                          Number(maxValue) || 0,
                          ...chartData.map((point) => Number(point.reviewCount) || 0)
                        );
                        const thresholdMax = Math.max(
                          Number(discoveryTarget) || 0,
                          Number(masteryTarget) || 0,
                          showRegressionLine ? Number(masteryRegressionTarget) || 0 : 0
                        );
                        return (
                          Math.max(seriesMax, thresholdMax) +
                          Math.max(2, Math.ceil(seriesMax * 0.05))
                        );
                      },
                    ]
              }
            />
            {skeleton ? null : <Tooltip content={<ProgressChartTooltip />} />}
            {Number.isFinite(discoveryTarget) && (skeleton || !hiddenItems.discoveryThreshold) && (
              <ReferenceLine
                y={discoveryTarget}
                stroke="#1f6b45"
                strokeDasharray="7 5"
                strokeWidth={1.5}
                strokeOpacity={0.85}
                label={(labelProps) => (
                  <ThresholdLineLabel
                    {...labelProps}
                    text={`Seuil de découverte (${discoveryTarget})`}
                    fill="#1f6b45"
                  />
                )}
              />
            )}
            {Number.isFinite(masteryTarget) && (skeleton || !hiddenItems.masteryThreshold) && (
              <ReferenceLine
                y={masteryTarget}
                stroke="#9a6b12"
                strokeDasharray="7 5"
                strokeWidth={1.5}
                strokeOpacity={0.85}
                label={(labelProps) => (
                  <ThresholdLineLabel
                    {...labelProps}
                    text={`Seuil de maîtrise (${masteryTarget})`}
                    fill="#9a6b12"
                  />
                )}
              />
            )}
            {showRegressionLine && (skeleton || !hiddenItems.masteryRegressionThreshold) && (
              <ReferenceLine
                y={masteryRegressionTarget}
                stroke="#991b1b"
                strokeDasharray="4 4"
                strokeWidth={1.5}
                strokeOpacity={0.9}
                label={(labelProps) => (
                  <ThresholdLineLabel
                    {...labelProps}
                    text={regressionLineLabel}
                    fill="#991b1b"
                  />
                )}
              />
            )}
            {skeleton
              ? null
              : SERIES.map((series) => (
                  <Line
                    key={series.key}
                    type="monotone"
                    dataKey={series.key}
                    name={series.name}
                    stroke={series.color}
                    strokeWidth={2.5}
                    dot={{ r: 3.5, stroke: "#faf0e6", strokeWidth: 1.5, fill: series.color }}
                    activeDot={{ r: 5 }}
                    hide={Boolean(hiddenItems[series.key])}
                    isAnimationActive={false}
                  />
                ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
